/** @jsx jsx */
import { gql } from '@apollo/client';
import { graphql } from 'gatsby';
import Image from 'gatsby-image';
import { jsx } from 'theme-ui';
import { getFixedImage } from '../../helpers/getImageProps';

const dimensions = {
  smaller: { width: 32, height: 32 },
  small: { width: [42, 84], height: [42, 84] },
  default: { width: [60, 120], height: [60, 120] },
  large: { width: [142, 184], height: [142, 184] },
};
const paddings = {
  smaller: { padding: 1 },
  small: { padding: [1, 2] },
  default: { boxShadow: undefined },
  large: { padding: [1, 2] },
};
export default function BrandLogo({
  brand,
  className = undefined,
  size = 'small',
  onDark = false,
}) {
  const { logo } = brand || {};
  const hasLogo = Boolean(logo?.fixed);
  return (
    <div
      sx={(theme) => ({
        backgroundColor: 'background',
        borderRadius: 'default',
        position: 'relative',
        ...dimensions[size],
        boxSizing: 'content-box',
        color: 'lighterText',
        boxShadow: onDark ? undefined : `0 0 0 1px ${theme.colors?.border}`,
        ...paddings[size],
      })}
      className={className}
    >
      {hasLogo && (
        <Image
          fixed={getFixedImage(logo.fixed)}
          alt={brand.name}
          style={{ width: null, height: null }}
          placeholderStyle={{ objectFit: null, filter: 'blur(3px)' }}
          sx={{
            borderRadius: 'default',
            ...dimensions[size],
            img: { objectFit: 'contain' },
          }}
        />
      )}
    </div>
  );
}

export const fragment = graphql`
  # The fragments for the Bottlebooks API
  #############################################
  fragment bb_BrandLogoLarge on Bottlebooks_AbstractBrand {
    name
    logo {
      fixed(
        width: 184
        height: 184
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        srcSet
        height
        width
        base64
      }
    }
  }

  fragment bb_BrandLogoDefault on Bottlebooks_AbstractBrand {
    name
    logo {
      fixed(
        width: 120
        height: 120
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        srcSet
        height
        width
        base64
      }
    }
  }

  # The fragments for the legacy pubCache API
  #############################################
  # Optimized for large lists (only sends the src to save bandwidth)
  fragment BrandLogoSmaller_list on AbstractBrand {
    name
    logo {
      fixed(
        width: 64
        height: 64
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        height
        width
      }
    }
  }
  fragment BrandLogoSmall on AbstractBrand {
    name
    logo {
      fixed(
        width: 84
        height: 84
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        srcSet
        height
        width
        base64
      }
    }
  }
  fragment BrandLogoDefault on AbstractBrand {
    name
    logo {
      fixed(
        width: 120
        height: 120
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        srcSet
        height
        width
        base64
      }
    }
  }

  # Optimized for large lists (only sends the src to save bandwidth)
  fragment BrandLogoSmall_list on AbstractBrand {
    name
    logo {
      fixed(
        width: 84
        height: 84
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        height
        width
      }
    }
  }
  fragment bb_BrandLogoSmall_list on Bottlebooks_AbstractBrand {
    name
    logo {
      fixed(
        width: 84
        height: 84
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        height
        width
      }
    }
  }
  fragment BrandLogoLarge on AbstractBrand {
    name
    logo {
      fixed(
        width: 184
        height: 184
        crop: PAD
        background: "#FFF0"
        quality: BEST
      ) {
        src
        srcSet
        height
        width
        base64
      }
    }
  }
`;

// Client-only pages need a separate fragment. Unfortunately both must be defined separately.
const logoSmall = gql`
  fragment Brand_logo_small on CloudinaryAsset {
    fixed(
      width: 84
      height: 84
      crop: PAD
      background: "#FFF0"
      quality: BEST
    ) {
      src
      srcSet
      height
      width
      base64
    }
  }
`;

const logoLarge = gql`
  fragment Brand_logo_large on CloudinaryAsset {
    fixed(
      width: 184
      height: 184
      crop: PAD
      background: "#FFF0"
      quality: BEST
    ) {
      src
      srcSet
      height
      width
      base64
    }
  }
`;

export const BrandLogoFragments = {
  small: gql`
    ${logoSmall}
    fragment BrandLogoSmall on AbstractBrand {
      name
      logo {
        ...Brand_logo_small
      }
    }
  `,

  large: gql`
    ${logoLarge}
    fragment BrandLogoLarge on AbstractBrand {
      name
      logo {
        ...Brand_logo_large
      }
    }
  `,
};
